import React, { useMemo, useState } from 'react';
import Modal from '../common/Modal';
import Table from '../common/Table';
import Button from '../common/Button';
import SearchBar from '../common/SearchBar';
import BusinessPermitApplicationModal from './NewBusinessPermitModal';
import UpdatePermitStatusModal from './UpdatePermitStatusModal';
import ViewApplicationModal from './ViewApplicationModal';
import {
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  EllipsisHorizontalIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useFetch } from '../../hooks/useCallApi';
import { businessPermitsAPI } from '../../services/api/admin/businessPermitApi';
import { showSuccess, showError } from '../../utils/notificationHelper';
import StatusBadge from '../common/StatusBadge';

const DEFAULT_FILTERS = {
  permitYearFrom:  '',
  permitYearTo:    '',
  applicationType: '',
  status:          '',
  businessId:      '',
  businessType:    '',
  classification:  '',
};

const PER_PAGE_OPTIONS = [5, 10];

const BusinessPermitApplicationsModal = ({ isOpen, onClose }) => {
  const [filtersOpen, setFiltersOpen]   = useState(false);
  const [filters, setFilters]           = useState(DEFAULT_FILTERS);
  const [searchValue, setSearchValue]   = useState('');
  const [currentPage, setCurrentPage]   = useState(1);
  const [perPage, setPerPage]           = useState(5);

  // ── Modal state ──
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationMode, setApplicationMode]           = useState('create');
  const [selectedApplication, setSelectedApplication]   = useState(null);
  const [showStatusModal, setShowStatusModal]           = useState(false);
  const [showViewModal, setShowViewModal]               = useState(false);

  const params = useMemo(() => ({
    per_page:         perPage,
    page:             currentPage,
    search:           searchValue || undefined,
    permit_year_from: filters.permitYearFrom || undefined,
    permit_year_to:   filters.permitYearTo   || undefined,
    application_type: filters.applicationType || undefined,
    status:           filters.status          || undefined,
    business_id:      filters.businessId     || undefined,
    business_type:    filters.businessType   || undefined,
    classification:   filters.classification  || undefined,
  }), [perPage, currentPage, searchValue, filters]);

  const { data: response, isLoading, refetch } = useFetch(
    ['permit-applications', params],
    () => businessPermitsAPI.getAllPermits(params),
    { keepPreviousData: true, staleTime: 30 * 1000 }
  );

  const tableData  = response?.data?.permit_applications ?? response?.items ?? (Array.isArray(response) ? response : []);

  // Handle pagination structure (data.meta) with fallback to direct access
  const paginationData = response?.data?.meta ?? response?.meta ?? response?.data?.pagination ?? response?.pagination;
  const totalItems = paginationData?.total ?? response?.data?.total ?? response?.meta?.total ?? tableData.length;
  const lastPage   = paginationData?.last_page ?? response?.data?.last_page ?? response?.meta?.last_page ??
                     Math.max(1, Math.ceil(totalItems / perPage));

  // ── Handlers ──
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const handleNewApplication = () => {
    setApplicationMode('create');
    setSelectedApplication(null);
    setShowApplicationModal(true);
  };

  const handleEditApplication = async (application) => {
    const id = application?.id ?? application?.permit_application_id;
    if (!id) {
      showError('Failed to open application for editing');
      return;
    }

    try {
      const res = await businessPermitsAPI.getPermitById(id);
      const details = res?.data?.data ?? res?.data ?? application;
      setApplicationMode('edit');
      setSelectedApplication(details);
      setShowApplicationModal(true);
    } catch {
      showError('Failed to load application details for editing');
    }
  };

  const handleViewApplication = async (application) => {
    const id = application?.id ?? application?.permit_application_id;
    if (!id) {
      showError('Failed to open application details');
      return;
    }

    try {
      const res = await businessPermitsAPI.getPermitById(id);
      const details = res?.data?.data ?? res?.data ?? application;
      setSelectedApplication(details);
      setShowViewModal(true);
    } catch {
      showError('Failed to load application details');
    }
  };

  const handleUpdateStatus = (application) => {
    setSelectedApplication(application);
    setShowStatusModal(true);
  };

  const handleApplicationSubmit = () => {
    refetch();
    setShowApplicationModal(false);
    setSelectedApplication(null);
  };

  const handleStatusUpdate = () => {
    refetch();
  };

  const closeApplicationModal = () => {
    setShowApplicationModal(false);
    setSelectedApplication(null);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedApplication(null);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedApplication(null);
  };

  const handleExport = async () => {
    try {
      const exportParams = {
        ...(filters.permitYearFrom  && { permit_year_from: filters.permitYearFrom }),
        ...(filters.permitYearTo    && { permit_year_to:   filters.permitYearTo }),
        ...(filters.applicationType && { application_type: filters.applicationType }),
        ...(filters.status          && { status:           filters.status }),
        ...(filters.businessId      && { business_id:      filters.businessId }),
        ...(filters.businessType    && { business_type:    filters.businessType }),
        ...(filters.classification  && { classification:   filters.classification }),
        ...(searchValue             && { search:           searchValue }),
      };

      const res = await businessPermitsAPI.exportPermits(exportParams);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'permit-applications.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Permit applications exported successfully');
    } catch {
      showError('Failed to export permit applications');
    }
  };

  // ── Table columns ──
  const columns = useMemo(() => [
    {
      key: 'referenceNo',
      label: 'REFERENCE NO.',
      render: (_, row) => row.application_ref_no || '',
    },
    {
      key: 'businessName',
      label: 'BUSINESS NAME',
      render: (_, row) => row.business?.business_name || '',
    },
    {
      key: 'applicationType',
      label: 'APPLICATION TYPE',
      render: (_, row) => row.application_type || '',
    },
    {
      key: 'permitYear',
      label: 'PERMIT YEAR',
      render: (_, row) => row.permit_year || '',
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (_, row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      render: (_, row) => (
        <div className="flex items-center justify-end space-x-1">
          <button
            type="button"
            aria-label="View"
            title="View Details"
            onClick={() => handleViewApplication(row)}
            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 hover:scale-110"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Edit"
            title="Edit"
            onClick={() => handleEditApplication(row)}
            className="text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50 transition-all duration-200 hover:scale-110"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="More"
            title="Update Status"
            onClick={() => handleUpdateStatus(row)}
            className="text-gray-600 hover:text-gray-900 p-1.5 rounded-full hover:bg-gray-50 transition-all duration-200 hover:scale-110"
          >
            <EllipsisHorizontalIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ], []);

  // ── Render ──
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Business Permit Applications" size="5xl">
      <div className="space-y-4">

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-[28rem]">
              <SearchBar
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search businesses by name, type, status..."
              />
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex items-center gap-2 px-5 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition"
            >
              <FunnelIcon className="h-5 w-5 text-gray-500" />
              <span>Filters</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              icon={<ArrowDownTrayIcon className="h-5 w-5" />}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button variant="primary" onClick={handleNewApplication} className="px-6">
              + New Application
            </Button>
          </div>
        </div>

        {/* Filters panel */}
        {filtersOpen && (
          <div className="border border-gray-200 rounded-xl p-4 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permit Year</label>
                <div className="flex items-center gap-2">
                  <input
                    value={filters.permitYearFrom}
                    onChange={(e) => setFilters((prev) => ({ ...prev, permitYearFrom: e.target.value }))}
                    placeholder="yyyy"
                    inputMode="numeric"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    value={filters.permitYearTo}
                    onChange={(e) => setFilters((prev) => ({ ...prev, permitYearTo: e.target.value }))}
                    placeholder="yyyy"
                    inputMode="numeric"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Application Type</label>
                <select
                  value={filters.applicationType}
                  onChange={(e) => setFilters((prev) => ({ ...prev, applicationType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">All Application Types</option>
                  <option value="New">New</option>
                  <option value="Renewal">Renewal</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permit Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">All Status</option>
                  <option value="Under Review">Under Review</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Endorsed">Endorsed</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Draft">Draft</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business ID</label>
                <input
                  value={filters.businessId}
                  onChange={(e) => setFilters((prev) => ({ ...prev, businessId: e.target.value }))}
                  placeholder="Enter Business ID"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Type</label>
                <input
                  value={filters.businessType}
                  onChange={(e) => setFilters((prev) => ({ ...prev, businessType: e.target.value }))}
                  placeholder="Enter Business Type"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Classification</label>
                <input
                  value={filters.classification}
                  onChange={(e) => setFilters((prev) => ({ ...prev, classification: e.target.value }))}
                  placeholder="Enter Classification"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-end gap-2 lg:col-span-2">
                <Button variant="outline" className="flex-1" onClick={clearFilters}>
                  Clear
                </Button>
                <Button variant="primary" className="flex-1" onClick={applyFilters}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <Table
          columns={columns}
          data={tableData}
          getRowKey={(row) => row.id ?? row.application_ref_no}
          pagination={{
            currentPage,
            lastPage,
            perPage,
            total: totalItems,
          }}
          onPageChange={handlePageChange}
          paginationProps={{
            showSizeSelector: true,
            sizeOptions:      PER_PAGE_OPTIONS,
            onPerPageChange:  handlePerPageChange,
            isLoading,
          }}
          isLoading={isLoading}
        />
      </div>

      <BusinessPermitApplicationModal
        isOpen={showApplicationModal}
        onClose={closeApplicationModal}
        mode={applicationMode}
        initialData={selectedApplication}
        onSubmit={handleApplicationSubmit}
      />

      <UpdatePermitStatusModal
        isOpen={showStatusModal}
        onClose={closeStatusModal}
        application={selectedApplication}
        onSubmit={handleStatusUpdate}
      />

      <ViewApplicationModal
        isOpen={showViewModal}
        onClose={closeViewModal}
        application={selectedApplication}
      />
    </Modal>
  );
};

export default BusinessPermitApplicationsModal;